// Main chat surface. Message list + composer. Each Q/A pair has an
// evidence panel and a feedback row underneath.

import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { askQuestionStreaming, uploadDocument } from '../lib/api'
import type { AskResponse, ChatAttachment, ChatScope } from '../lib/types'
import { EvidencePanel } from './Evidence'
import { FeedbackButtons } from './FeedbackButtons'
import { Thinking } from './Thinking'

export interface ChatTurn {
  id: string
  question: string
  response: AskResponse | null
  loading: boolean
  error: string | null
  /** Live progress while the answer is being produced (streaming only). */
  progress?: string
  /** Answer text as it streams in, before the final result arrives. */
  partial?: string
}

interface ChatProps {
  // Fired when a new turn completes, so the sidebar history can refresh.
  onTurnComplete?: () => void
  // If the sidebar loaded a prior audit, we prefill it here as a read-only
  // preview. Defined = viewing history mode. Undefined = live conversation.
  restoredTurns?: ChatTurn[]
  // Called when the user leaves the restored view (via "New conversation"
  // or by sending a new message). Lets the parent clear the restored state
  // so subsequent interactions are a fresh conversation.
  onExitRestored?: () => void
  // Set with restoredTurns when they are a whole conversation (2026-09-30). The next
  // question then CONTINUES it — same id, earlier turns as prior_turns — rather than
  // clearing it. Null/undefined for pre-threading rows, which stay read-only reference.
  restoredConversationId?: string | null
  // Set when this conversation was opened from a demo card. The opportunity's context has
  // already been warmed server-side, so questions answer without first resolving the
  // account. It is a hint, not a filter — the rep can still ask about anything.
  scope?: ChatScope | null
  // Leave the scoped conversation and go back to an ordinary one.
  onClearScope?: () => void
  // A document attached to this conversation (2026-09-30). Its id goes with every question
  // until it is cleared; the text itself stays on the server. Held by the parent so it
  // survives a trip to another view.
  attachment?: ChatAttachment | null
  onAttachmentChange?: (att: ChatAttachment | null) => void
  // Text another view wants in the composer (the My context page's "Run in chat").
  pendingPrompt?: string | null
  onPendingPromptConsumed?: () => void
}

export function Chat({
  onTurnComplete, restoredTurns, onExitRestored, restoredConversationId, scope, onClearScope,
  attachment, onAttachmentChange, pendingPrompt, onPendingPromptConsumed,
}: ChatProps) {
  const [turns, setTurns] = useState<ChatTurn[]>(restoredTurns || [])
  const [input, setInput] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Consume a prompt handed over by another view exactly once.
  useEffect(() => {
    if (pendingPrompt) {
      setInput(pendingPrompt)
      onPendingPromptConsumed?.()
      inputRef.current?.focus()
    }
  }, [pendingPrompt, onPendingPromptConsumed])
  // Sent with every question so the audit log groups turns into threads. Rotated on
  // "New conversation"; replaced by the thread's own id when one is reopened from the
  // sidebar. Before 2026-09-30 nothing was sent and every turn logged as its own island.
  const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID())
  const scrollRef = useRef<HTMLDivElement>(null)

  const isRestored = restoredTurns !== undefined
  // A reopened thread continues; a lone pre-threading turn is reference only.
  const continuing = isRestored && !!restoredConversationId

  useEffect(() => {
    if (restoredTurns) {
      setTurns(restoredTurns)
      // Continue the reopened thread under its own id. A pre-threading row gets a fresh
      // id now, so whatever is asked after viewing it starts a clean thread.
      setConversationId(restoredConversationId || crypto.randomUUID())
    }
  }, [restoredTurns, restoredConversationId])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [turns.length, turns[turns.length - 1]?.loading])

  async function send() {
    const q = input.trim()
    if (!q) return
    const turnId = crypto.randomUUID()

    // Viewing a restored entry and the user types a question. Two cases:
    //   - a reopened conversation (has a conversation id): CONTINUE it — keep the turns,
    //     they become prior_turns, the same id goes on the log;
    //   - a lone pre-threading turn: it was shown as reference, not to be continued, so
    //     exit restored mode and start fresh without carrying it into prior_turns.
    const startFresh = isRestored && !continuing
    if (isRestored) {
      onExitRestored?.()
      if (startFresh) setTurns([])
    }

    // Build prior_turns from the completed successful turns in this
    // conversation. This is what enables follow-ups like "elaborate on
    // that" or "what about Q1 instead?" — Claude gets the full context.
    // We cap at the last 8 turns to keep cost bounded (each turn is
    // roughly 500-2000 input tokens depending on answer length).
    const priorTurns = (startFresh ? [] : turns)
      .filter((t) => t.response && t.response.ok && t.response.answer)
      .slice(-8)
      .map((t) => ({ question: t.question, answer: t.response!.answer }))

    const newTurn: ChatTurn = { id: turnId, question: q, response: null, loading: true, error: null }
    setTurns((t) => (startFresh ? [newTurn] : [...t, newTurn]))
    setInput('')

    try {
      const resp = await askQuestionStreaming(
        q,
        (evt) => {
          // Progress and partial text land on the in-flight turn so the user sees
          // the work happening instead of a spinner. The final `done` event still
          // replaces this with the complete result below.
          if (evt.type === 'tool_start') {
            setTurns((t) =>
              t.map((turn) => (turn.id === turnId ? { ...turn, progress: evt.label } : turn)),
            )
          } else if (evt.type === 'text') {
            setTurns((t) =>
              t.map((turn) =>
                turn.id === turnId
                  ? { ...turn, partial: (turn.partial ?? '') + evt.text, progress: undefined }
                  : turn,
              ),
            )
          }
        },
        {
          conversationId,
          priorTurns: priorTurns.length > 0 ? priorTurns : undefined,
          opportunityId: scope?.opportunityId,
          documentId: attachment?.documentId,
        },
      )
      setTurns((t) =>
        t.map((turn) =>
          turn.id === turnId
            ? { ...turn, response: resp, loading: false, error: resp.error, partial: undefined, progress: undefined }
            : turn,
        ),
      )
    } catch (e) {
      setTurns((t) =>
        t.map((turn) =>
          turn.id === turnId
            ? { ...turn, loading: false, error: e instanceof Error ? e.message : String(e) }
            : turn,
        ),
      )
    } finally {
      onTurnComplete?.()
    }
  }

  function newConversation() {
    setTurns([])
    setInput('')
    setConversationId(crypto.randomUUID())
    if (isRestored) onExitRestored?.()
    // "New conversation" means new, scope included. Leaving a deal attached to a thread
    // the rep believes they just cleared is the one way this feature could quietly answer
    // about the wrong account.
    onClearScope?.()
    // Same for an attachment: a document belongs to the conversation it was added to.
    onAttachmentChange?.(null)
    setUploadError(null)
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      send()
    }
  }

  // Upload the picked file and attach it. Errors are the server's own words (wrong type,
  // too large, nothing readable) and stay visible under the composer until the next try:
  // a document that quietly failed to attach is an answer that quietly lacks it.
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setUploading(true)
    setUploadError(null)
    try {
      const doc = await uploadDocument(f)
      onAttachmentChange?.({
        documentId: doc.key,
        title: doc.title || f.name,
        approxTokens: doc.approx_tokens,
        truncated: !!doc.meta?.truncated,
      })
    } catch (err) {
      setUploadError((err instanceof Error ? err.message : String(err)).replace(/^HTTP \d+: /, ''))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar with new-conversation control */}
      <div className="flex items-center justify-between px-6 py-2 border-b border-marker-border bg-marker-panel">
        <div className="text-sm text-marker-muted flex items-center gap-2">
          {isRestored ? (
            <span className="flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 rounded bg-marker-accent/20 text-marker-accent text-xs font-medium">
                {continuing ? 'Past conversation' : 'Viewing past question'}
              </span>
              <span>
                {continuing
                  ? 'Type below to continue it, or click "+ New conversation" to start fresh'
                  : 'Type below or click "+ New conversation" to start a fresh thread'}
              </span>
            </span>
          ) : scope ? (
            // Named, and dismissible. A rep needs to see which deal the chat is holding —
            // an invisible scope is how you end up reading one account's answer as
            // another's — and needs a way out of it without losing the thread.
            <span className="flex items-center gap-2 min-w-0">
              <span className="inline-block px-2 py-0.5 rounded bg-marker-accent/20 text-marker-accent text-xs font-medium whitespace-nowrap">
                {scope.kind === 'follow_up' ? 'Follow-up demo' : 'First demo'}
                {scope.time ? ` · ${scope.time}` : ''}
              </span>
              <span className="truncate text-marker-text" title={scope.title || scope.account}>
                {scope.account}
              </span>
              <span className="text-marker-muted whitespace-nowrap">— context loaded</span>
              <button
                onClick={onClearScope}
                title="Keep this conversation but stop scoping it to this deal"
                className="text-xs text-marker-muted hover:text-marker-text underline decoration-dotted whitespace-nowrap"
              >
                clear
              </button>
            </span>
          ) : turns.length === 0 ? (
            <span>New conversation</span>
          ) : (
            <span>
              {turns.length} turn{turns.length === 1 ? '' : 's'} · Claude sees the full conversation
            </span>
          )}
          {/* The attachment is named and dismissible for the same reason the scope is: the
              user must be able to see what the model is reading alongside their question. */}
          {attachment && (
            <span className="flex items-center gap-2 min-w-0 ml-2 pl-2 border-l border-marker-border">
              <span className="truncate text-marker-text" title={attachment.title}>
                📎 {attachment.title}
              </span>
              {attachment.approxTokens ? (
                <span className="text-marker-muted whitespace-nowrap">~{attachment.approxTokens.toLocaleString()} tokens</span>
              ) : null}
              {attachment.truncated && (
                <span className="text-marker-bad whitespace-nowrap" title="The document was longer than the limit; the end is not included.">
                  cut off
                </span>
              )}
              <button
                onClick={() => onAttachmentChange?.(null)}
                title="Stop attaching this document to the conversation"
                className="text-xs text-marker-muted hover:text-marker-text underline decoration-dotted whitespace-nowrap"
              >
                remove
              </button>
            </span>
          )}
        </div>
        <button
          onClick={newConversation}
          disabled={turns.length === 0}
          className="text-xs px-3 py-1 rounded border border-marker-border hover:border-marker-accent hover:text-marker-accent disabled:opacity-40 disabled:cursor-not-allowed"
          title="Start a fresh conversation (clears context Claude sees)"
        >
          + New conversation
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4">
        {/* Scoped and empty: the rep is most likely about to walk into this demo, so the
            opening screen is the deal's name and four things worth asking about it rather
            than the general prompts, which are about the business as a whole and useless
            with two minutes before a call. */}
        {turns.length === 0 && scope && (
          <div className="max-w-2xl mx-auto mt-14">
            <div className="text-center mb-6">
              <div className="text-2xl font-semibold text-marker-text mb-1">{scope.account}</div>
              <div className="text-sm text-marker-muted">
                This deal's calls, emails and Salesforce history are loaded. Ask anything —
                about this account or not.
              </div>
            </div>
            <div className="text-left space-y-2 text-sm">
              <div className="text-marker-muted mb-2">Try:</div>
              <ExampleQuestion setInput={setInput}>
                What did we agree to follow up on after the last call?
              </ExampleQuestion>
              <ExampleQuestion setInput={setInput}>
                What questions did they ask that we didn't answer?
              </ExampleQuestion>
              <ExampleQuestion setInput={setInput}>
                Which competitors have come up, and what did they say about them?
              </ExampleQuestion>
              <ExampleQuestion setInput={setInput}>
                What are their biggest concerns so far, in their own words?
              </ExampleQuestion>
            </div>
          </div>
        )}
        {turns.length === 0 && !scope && (
          <div className="max-w-2xl mx-auto mt-16 text-center">
            <div className="text-2xl font-semibold text-marker-text mb-2">
              Ask a question about our sales calls & pipeline
            </div>
            <div className="text-sm text-marker-muted mb-6">
              Marker draws from every Gong-recorded call transcript, Salesforce opportunity data,
              and prospect emails from 2026. Every answer cites its sources.
            </div>
            <div className="text-left space-y-2 text-sm">
              <div className="text-marker-muted mb-2">Try:</div>
              <ExampleQuestion setInput={setInput}>
                Which AEs have the highest displacement rate vs. Dentrix incumbents YTD?
              </ExampleQuestion>
              <ExampleQuestion setInput={setInput}>
                What have prospects said about Archy's pricing in June demos?
              </ExampleQuestion>
              <ExampleQuestion setInput={setInput}>
                Show me all opportunities where the prospect explicitly said they chose a competitor
              </ExampleQuestion>
              <ExampleQuestion setInput={setInput}>
                Summarize the objection themes for Drew Ward's Open Dental demos in the last 90 days
              </ExampleQuestion>
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto space-y-8">
          {turns.map((turn) => (
            <div key={turn.id}>
              {/* User question */}
              <div className="flex justify-end mb-3">
                <div className="bg-marker-accent text-white px-4 py-2 rounded-lg max-w-2xl">
                  <div className="whitespace-pre-wrap">{turn.question}</div>
                </div>
              </div>

              {/* Answer */}
              <div className="bg-marker-panel border border-marker-border rounded-lg p-4">
                {turn.loading && <Thinking progress={turn.progress} partial={turn.partial} />}
                {turn.error && !turn.response && (
                  <div className="text-marker-bad text-sm">
                    <div className="font-semibold">Error:</div>
                    <div className="font-mono text-xs mt-1">{turn.error}</div>
                  </div>
                )}
                {/* Failed response (HTTP 200 but ok:false — e.g. the AI
                    provider erroring). Show a clear message instead of a
                    blank answer, and never swallow the error. */}
                {turn.response && !turn.response.ok && (
                  <div className="text-marker-bad text-sm">
                    <div className="font-semibold">Marker couldn't complete this request.</div>
                    <div className="text-marker-muted mt-1">
                      The AI service hit an error — please try again in a moment. If it keeps
                      happening, let the team know.
                    </div>
                    {(turn.response.error || turn.error) && (
                      <div className="font-mono text-xs mt-2 text-marker-muted break-words">
                        {turn.response.error || turn.error}
                      </div>
                    )}
                  </div>
                )}
                {turn.response && turn.response.ok && (
                  <>
                    <div className="prose prose-marker prose-invert max-w-none text-marker-text">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {turn.response.answer || '_(No answer was returned.)_'}
                      </ReactMarkdown>
                    </div>
                    <EvidencePanel resp={turn.response} />
                    <FeedbackButtons auditId={turn.response.audit_id} />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-marker-border bg-marker-panel p-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={attachment
                ? `Ask about ${attachment.title} — or anything else. Ctrl+Enter to send.`
                : 'Ask anything about our sales calls, opportunities, or competitors. Press Ctrl+Enter (or Cmd+Enter) to send.'}
              rows={3}
              className="w-full bg-marker-bg border border-marker-border rounded-lg p-3 pr-24 text-marker-text placeholder:text-marker-muted focus:outline-none focus:border-marker-accent resize-none"
            />
            <button
              onClick={send}
              disabled={!input.trim()}
              className="absolute bottom-3 right-3 px-4 py-1.5 bg-marker-accent hover:bg-marker-accent2 disabled:bg-marker-panel2 disabled:text-marker-muted disabled:cursor-not-allowed text-white rounded font-medium"
            >
              Send
            </button>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <div className="flex items-center gap-3 min-w-0">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.txt,.md"
                className="hidden"
                onChange={(e) => void onPickFile(e)}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                title="Attach a PDF, Word, text or markdown file (up to 15 MB) to this conversation"
                className="text-marker-muted hover:text-marker-text disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : attachment ? '📎 Replace attachment' : '📎 Attach a document'}
              </button>
              {uploadError && (
                <span className="text-marker-bad truncate" title={uploadError}>{uploadError}</span>
              )}
            </div>
            <div className="text-marker-muted whitespace-nowrap">
              Ctrl+Enter to send
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ExampleQuestion({
  children,
  setInput,
}: {
  children: string
  setInput: (s: string) => void
}) {
  return (
    <button
      onClick={() => setInput(children)}
      className="block w-full text-left px-3 py-2 border border-marker-border rounded-lg hover:border-marker-accent hover:bg-marker-panel text-marker-text"
    >
      {children}
    </button>
  )
}
